import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Gift, RefreshCcw } from "lucide-react";
import { ReferralProgress } from "@/components/ReferralApprovalsCard";

const FILTERS = [["pending", "Pending referrals"], ["", "All"], ["credited", "Credited"], ["expired", "Expired"]];
const STATUS_CLS = {
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  credited: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  expired: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};

function CreditDialog({ target, isAdmin, onClose, onDone }) {
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) { setPoints(String(target.points)); setReason(""); } }, [target]);
  if (!target) return null;
  const both = target.side === "both";
  const changed = isAdmin && !both && Number(points) !== target.points;
  const submit = async () => {
    setBusy(true);
    try {
      const body = { side: target.side, reason: reason || undefined };
      if (changed) body.points = Number(points);
      const { data } = await api.post(`/referrals/${target.referral_id}/credit`, body);
      toast.success(data.pending_approval ? "Submitted for Admin/Manager approval" : "Referral points credited");
      onDone();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="referral-credit-dialog">
        <DialogHeader>
          <DialogTitle>{both ? "Credit both sides" : `Credit ${target.side} points`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <p style={{ color: "var(--text-muted)" }}>
            Use this only when the referee's first purchase happened but the system could not see it
            (for example, a booking not recorded in the app).
            {!isAdmin && " Your request goes to an Admin or Manager for approval."}
          </p>
          {both ? (
            <div className="rounded-lg p-3" style={{ background: "var(--bg-soft)" }} data-testid="referral-credit-both-summary">
              Referrer: <b>{target.referrerPts} pts</b> · Referee: <b>{target.refereePts} pts</b>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Points ({target.member_name})</Label>
              <Input type="number" min={1} value={points} disabled={!isAdmin}
                     onChange={(e) => setPoints(e.target.value)} data-testid="referral-credit-points" />
              {!isAdmin && <p className="text-xs" style={{ color: "var(--text-muted)" }}>Set by Admin. Staff cannot change the amount.</p>}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Reason{changed ? " (required when changing the amount)" : " (optional)"}</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="referral-credit-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || (changed && reason.trim().length < 3) || Number(points) <= 0}
                  className="bg-navy text-white" data-testid="referral-credit-confirm">
            {busy ? "Saving…" : isAdmin ? "Credit now" : "Submit for approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ r, first, canCredit, onCredit, threshold }) {
  const pending = r.status === "pending";
  const awaiting = !!r.pending_tx;
  const clickable = pending && canCredit && !awaiting;
  return (
    <tr className={first ? "border-t-2" : "border-t"} style={{ borderColor: "var(--border)" }}
        data-testid={`referral-row-${r.referral_id}-${r.side}`}>
      <td className="px-4 py-2.5">
        <button type="button" disabled={!clickable} onClick={() => onCredit(r)}
                className={`text-[11px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${clickable ? "hover:underline cursor-pointer" : "cursor-default"}`}
                style={{ background: r.side === "referrer" ? "var(--navy)" : "var(--gold)",
                         color: r.side === "referrer" ? "#fff" : "var(--navy)" }}
                data-testid={`referral-label-${r.referral_id}-${r.side}`}>
          {r.side === "referrer" ? "Referrer" : "Referee"}
        </button>
      </td>
      <td className="px-4 py-2.5">
        <div className="font-medium">{r.member_name}</div>
        <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{r.member_id}</div>
      </td>
      <td className="px-4 py-2.5 text-xs" style={{ color: "var(--text-muted)" }}>
        {r.side === "referrer" ? "referred" : "referred by"} {r.counterpart_name}
      </td>
      <td className="px-4 py-2.5 text-right font-semibold">{r.points}</td>
      <td className="px-4 py-2.5">
        <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_CLS[r.status]}`}
              data-testid={`referral-status-${r.referral_id}-${r.side}`}>{r.status}</span>
        {pending && <span className="ml-2 text-xs" style={{ color: "var(--text-muted)" }}
                          data-testid={`referral-days-${r.referral_id}-${r.side}`}>{r.days_left} days left</span>}
        {awaiting && <div className="text-[11px] mt-1 text-amber-700">Awaiting approval</div>}
        {pending && r.approval === "eligible" && <div className="text-[11px] mt-1 text-emerald-700 font-semibold" data-testid={`referral-eligible-${r.referral_id}-${r.side}`}>Eligible for approval</div>}
        {pending && r.approval === "rejected" && <div className="text-[11px] mt-1 text-red-700" data-testid={`referral-rejected-${r.referral_id}-${r.side}`}>Rejected, can be reviewed again</div>}
        {pending && threshold > 0 && first && <div className="mt-1.5"><ReferralProgress progress={r.progress_aed} threshold={threshold} testid={`referral-progress-${r.referral_id}`} /></div>}
      </td>
      <td className="px-4 py-2.5 text-right">
        {clickable && (
          <Button size="sm" className="rounded-full h-8 text-white" style={{ background: "var(--gold)" }}
                  onClick={() => onCredit(r)} data-testid={`referral-credit-${r.referral_id}-${r.side}`}>
            <Gift className="h-3.5 w-3.5 mr-1" /> Credit points
          </Button>
        )}
      </td>
    </tr>
  );
}

export default function ReferralsPanel({ onChanged }) {
  const { user, hasPerm } = useAuth();
  const isAdmin = user?.role === "admin";
  const canCredit = hasPerm("earn");
  const [filter, setFilter] = useState("pending");
  const [data, setData] = useState({ rows: [], pending_count: 0, points: {} });
  const [target, setTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/referrals", { params: filter ? { status: filter } : {} });
      setData(data);
    } catch (e) { toast.error(formatError(e)); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const groups = data.rows.reduce((acc, r) => {
    (acc[r.referral_id] = acc[r.referral_id] || []).push(r);
    return acc;
  }, {});

  return (
    <section className="mb-8" data-testid="referrals-panel">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h2 className="text-xl font-bold flex items-center gap-2" style={{ letterSpacing: "-0.01em" }}>
          Referrals
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                data-testid="pending-referrals-badge">{data.pending_count} pending</span>
        </h2>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--bg-soft)" }}>
            {FILTERS.map(([k, l]) => (
              <button key={l} type="button" onClick={() => setFilter(k)} data-testid={`referral-filter-${k || "all"}`}
                      className={`px-3 h-8 rounded-full text-xs font-medium transition-colors ${filter === k ? "bg-navy text-white" : ""}`}>
                {l}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="referrals-refresh">
            <RefreshCcw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="w-card w-card-bordered overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--bg-soft)" }}>
              {["Side", "Member", "Referral", "Points", "Status", ""].map((h, i) => (
                <th key={i} className={`px-4 py-3 label-caps ${h === "Points" ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center" style={{ color: "var(--text-muted)" }}>No referrals here.</td></tr>
            )}
            {Object.entries(groups).map(([rid, rows]) => {
              const creditable = rows.filter((r) => r.status === "pending" && !r.pending_tx);
              return (
                <React.Fragment key={rid}>
                  {rows.map((r, i) => <Row key={r.side} r={r} first={i === 0} canCredit={canCredit} onCredit={setTarget} threshold={data.threshold_aed} />)}
                  {canCredit && creditable.length === 2 && (
                    <tr><td colSpan={6} className="px-4 pb-2.5 text-right">
                      <Button size="sm" variant="outline" className="rounded-full h-7 text-xs"
                              onClick={() => setTarget({ referral_id: rid, side: "both",
                                referrerPts: data.points.referrer, refereePts: data.points.referee })}
                              data-testid={`referral-credit-both-${rid}`}>
                        <Gift className="h-3 w-3 mr-1" /> Credit both
                      </Button>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {target && (
        <CreditDialog target={target} isAdmin={isAdmin} onClose={() => setTarget(null)}
                      onDone={() => { setTarget(null); load(); onChanged?.(); }} />
      )}
    </section>
  );
}
